/**
 * Práce s časem v pásmu Europe/Prague.
 * Jádro počítá v absolutních ms (UTC); jízdní řády jsou v lokálním
 * nástěnném čase, proto převody řešíme přes Intl API (správně řeší DST).
 */

export const TZ = 'Europe/Prague';

const partsFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export interface LocalParts {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** Rozklad absolutního času na lokální složky v Europe/Prague. */
export function pragueParts(ms: number): LocalParts {
  const parts = partsFmt.formatToParts(new Date(ms));
  const get = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  // Intl může vrátit hour 24 o půlnoci — normalizujeme na 0.
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

/** Minuty od lokální půlnoci v Praze (0–1439). */
export function pragueMinutesOfDay(ms: number): number {
  const p = pragueParts(ms);
  return p.hour * 60 + p.minute + p.second / 60;
}

/**
 * Absolutní čas (ms) odpovídající lokálnímu pražskému času.
 * Při jarní změně času (neexistující hodina) vrací nejbližší platný čas po ní;
 * při podzimní (dvojitá hodina, kdy lokální čas nastane dvakrát) vrací PRVNÍ
 * (dřívější) výskyt — to je pro cestujícího konzervativní volba.
 */
export function msFromPragueLocal(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): number {
  // První odhad: jako by lokální čas byl UTC.
  let guess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  // Dvě iterace korekce podle skutečného offsetu — konverguje pro reálná pásma.
  for (let i = 0; i < 2; i++) {
    const p = pragueParts(guess);
    const haveUtcLike = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    const wantUtcLike = Date.UTC(year, month - 1, day, hour, minute, 0);
    guess += wantUtcLike - haveUtcLike;
  }
  // Podzimní dvojitá hodina: iterace skončí na pozdějším výskytu. Když i o hodinu
  // dříve sedí stejné požadované lokální složky, je to první výskyt — preferujeme ho.
  const earlier = guess - 3_600_000;
  const pe = pragueParts(earlier);
  if (
    pe.year === year &&
    pe.month === month &&
    pe.day === day &&
    pe.hour === hour % 24 &&
    pe.minute === minute
  ) {
    return earlier;
  }
  return guess;
}

/**
 * Memoizovaná varianta msFromPragueLocal pro horké smyčky (Dijkstra nad
 * jízdním řádem volá převod tisíckrát pro stejné kombinace den+čas;
 * každé volání jinak stojí dva průchody Intl.formatToParts).
 */
const localMsCache = new Map<number, number>();

/** Memoizovaný rozklad času (minutové rozlišení) — pro horké smyčky řádů. */
const partsCache = new Map<number, LocalParts>();

export function pragueDateCached(ms: number): LocalParts {
  const key = Math.floor(ms / 60_000);
  let p = partsCache.get(key);
  if (!p) {
    p = pragueParts(ms);
    if (partsCache.size > 50_000) partsCache.clear();
    partsCache.set(key, p);
  }
  return p;
}

export function msFromPragueLocalCached(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): number {
  // Kompaktní číselný klíč (žádné string alokace v horké smyčce).
  const key = (((year * 13 + month) * 32 + day) * 25 + hour) * 60 + minute;
  let ms = localMsCache.get(key);
  if (ms === undefined) {
    ms = msFromPragueLocal(year, month, day, hour, minute);
    if (localMsCache.size > 100_000) localMsCache.clear();
    localMsCache.set(key, ms);
  }
  return ms;
}

/** „HH:MM" → minuty od půlnoci; vrací null pro neplatný vstup. */
export function parseHHMM(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Minuty od půlnoci → „HH:MM". */
export function formatHHMM(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

const timeFmt = new Intl.DateTimeFormat('cs-CZ', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
});

const dateTimeFmt = new Intl.DateTimeFormat('cs-CZ', {
  timeZone: TZ,
  day: 'numeric',
  month: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Lokální čas „14:05". */
export function formatTime(ms: number): string {
  return timeFmt.format(new Date(ms));
}

/** Lokální datum a čas „29. 3. 14:05". */
export function formatDateTime(ms: number): string {
  return dateTimeFmt.format(new Date(ms));
}

/** Trvání v minutách → lidský zápis „1 h 25 min" / „45 min" / „< 1 min". */
export function formatDuration(minutes: number): string {
  if (!Number.isFinite(minutes)) return '—';
  if (minutes < 1) return '< 1 min';
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** Spadají dva absolutní časy do téhož pražského dne? */
export function samePragueDay(aMs: number, bMs: number): boolean {
  const a = pragueParts(aMs);
  const b = pragueParts(bMs);
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/**
 * O kolik lokálních (pražských) kalendářních dnů je `bMs` později než `aMs`.
 * 0 = týž den, 1 = následující den… Používá lokální datum, ne 24h okna,
 * takže 23:55 → 00:10 vrací 1.
 */
export function pragueDayDiff(aMs: number, bMs: number): number {
  const a = pragueParts(aMs);
  const b = pragueParts(bMs);
  const aDays = Date.UTC(a.year, a.month - 1, a.day) / 86_400_000;
  const bDays = Date.UTC(b.year, b.month - 1, b.day) / 86_400_000;
  return Math.round(bDays - aDays);
}

/** Přípona „(+1 den)" / „(+2 dny)" pro příjezd v jiný den; prázdná pro týž den. */
export function dayOffsetSuffix(departMs: number, arriveMs: number): string {
  const d = pragueDayDiff(departMs, arriveMs);
  if (d <= 0) return '';
  if (d === 1) return ' (+1 den)';
  return d <= 4 ? ` (+${d} dny)` : ` (+${d} dní)`;
}

/**
 * Nejbližší odjezd podle jízdního řádu.
 * @param arrivalMs   absolutní čas příchodu na zastávku
 * @param departures  odjezdy v minutách od lokální půlnoci, vzestupně
 * @returns absolutní čas odjezdu (ms), nebo null pokud řád nemá žádný odjezd
 *
 * Překročení půlnoci: pokud dnes už nic nejede, vezme se první zítřejší odjezd.
 */
export function nextDeparture(
  arrivalMs: number,
  departures: number[],
  assumeSorted = false,
): number | null {
  if (departures.length === 0) return null;
  const sorted = assumeSorted ? departures : [...departures].sort((x, y) => x - y);
  const p = pragueDateCached(arrivalMs);

  // Kandidáty (dnešní + zítřejší kvůli překročení půlnoci) namapujeme na absolutní
  // ms a vybereme skutečné minimum ≥ příchodu. Mapování wall-time → ms není přes
  // DST mezeru monotónní, takže nestačí brát první v pořadí podle minut.
  let best = Infinity;
  for (const dayOffset of [0, 1]) {
    for (const dep of sorted) {
      const ms = msFromPragueLocalCached(p.year, p.month, p.day + dayOffset, Math.floor(dep / 60), dep % 60);
      if (ms >= arrivalMs - 30_000 && ms < best) best = ms;
      // Seřazené kandidáty dnešního dne lze po nalezení prvního ≥ příchodu
      // uříznout jen při dni 0 — zítřejší minimum je vždy první prvek,
      // ale přes DST mezeru to neplatí zaručeně; necháme plný průchod
      // (memoizace převodu dělá průchod levným).
    }
  }
  return Number.isFinite(best) ? best : null;
}

/**
 * Odjezdy linky jako minuty od půlnoci.
 * Explicitní odjezdy mají přednost; jinak se generují z intervalu
 * v okně firstDep–lastDep (výchozí 05:00–23:00, interval 15 min).
 */
export function lineDepartureMinutes(line: {
  departures?: string[];
  headwayMin?: number;
  firstDep?: string;
  lastDep?: string;
}): number[] {
  if (line.departures && line.departures.length > 0) {
    return line.departures
      .map(parseHHMM)
      .filter((x): x is number => x !== null)
      .sort((a, b) => a - b);
  }
  const headway = Math.max(1, line.headwayMin ?? 15);
  const first = parseHHMM(line.firstDep ?? '') ?? 5 * 60;
  const last = parseHHMM(line.lastDep ?? '') ?? 23 * 60;
  const out: number[] = [];
  // Okno přes půlnoc (např. 22:00–01:00) rozvineme do minut > 1440 a normalizujeme.
  const end = last >= first ? last : last + 1440;
  for (let t = first; t <= end; t += headway) out.push(t % 1440);
  return out.sort((a, b) => a - b);
}

/** Očekávané čekání pro hromadné (zónové) výpočty: polovina intervalu. */
export function expectedWaitMin(line: {
  departures?: string[];
  headwayMin?: number;
  firstDep?: string;
  lastDep?: string;
}): number {
  if (line.departures && line.departures.length > 1) {
    const mins = lineDepartureMinutes(line);
    let sum = 0;
    for (let i = 1; i < mins.length; i++) sum += mins[i] - mins[i - 1];
    return sum / (mins.length - 1) / 2;
  }
  if (line.departures && line.departures.length === 1) return 30; // jediný spoj denně — hrubý odhad
  return Math.max(1, line.headwayMin ?? 15) / 2;
}

/**
 * Očekávané čekání na linku v konkrétní minutě dne (časově závislé zóny).
 * Explicitní odjezdy → čekání na nejbližší spoj (přes půlnoc na první ranní);
 * intervalová linka → polovina intervalu v provozní době, jinak čekání
 * do začátku provozu. Aproximace: odjezdy se berou z výchozí zastávky linky.
 */
export function expectedWaitAtMin(
  line: { departures?: string[]; headwayMin?: number; firstDep?: string; lastDep?: string },
  minutesOfDay: number,
): number {
  const t = ((minutesOfDay % 1440) + 1440) % 1440;
  if (line.departures && line.departures.length > 0) {
    const mins = lineDepartureMinutes(line);
    let best = Infinity;
    for (const d of mins) {
      const wait = d >= t ? d - t : d + 1440 - t;
      if (wait < best) best = wait;
    }
    return Number.isFinite(best) ? best : 999;
  }
  const headway = Math.max(1, line.headwayMin ?? 15);
  const first = parseHHMM(line.firstDep ?? '') ?? 5 * 60;
  const last = parseHHMM(line.lastDep ?? '') ?? 23 * 60;
  const end = last >= first ? last : last + 1440;
  // V provozní době (vč. okna přes půlnoc): průměrné čekání = polovina intervalu.
  if ((t >= first && t <= end) || (end > 1440 && t + 1440 <= end)) return headway / 2;
  // Mimo provoz: čekání do prvního spoje.
  return t < first ? first - t : first + 1440 - t;
}
